import React from 'react';
const { useState, useEffect, useRef, useCallback } = React;
import { ownerCall } from './shared.jsx';
import { createOutbox, noticeState, optimistic, stopBody } from '../../timerClient.js';

// El contador de Mi espacio ("Trabajar en esto"), visible en toda la plataforma
// para la dueña. El estado vive en el servidor (shared/ownerTimer.ts): esta barra
// solo lo muestra y envía las acciones con el momento en que Ale actuó.
// - Recargar, cerrar o abrir otra pestaña no pierde el tramo: se vuelve a leer.
//   Las pestañas se avisan entre sí (BroadcastChannel) y releen al volver a la vista.
// - Sin conexión, la acción queda guardada en este navegador con su momento y se
//   envía al recuperar la conexión; el servidor no la duplica.
// - Nunca se detiene ni descuenta tiempo solo. Ocultar la pestaña o trabajar fuera
//   de Oike no es tiempo sin trabajar: no hay avisos por eso. Solo un aviso
//   discreto cada `noticeMinutes` (90 por defecto) y la marca de "por confirmar"
//   al cerrar un tramo de más de `reviewMinutes` (4 h por defecto).
// Otras pantallas inician o cambian de tarea con el evento `oike:timer` y leen el
// estado en `oike:timer-state`.

const OUTBOX = 'oike_timer_outbox';
const ACK = 'oike_timer_notice_ack';
const tz = () => { try { const v = localStorage.getItem('oike_myday_tz'); if (v) return v; } catch { /* default */ } try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } };
const readJson = (k, fb) => { try { return JSON.parse(localStorage.getItem(k) || '') ?? fb; } catch { return fb; } };
const writeJson = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* per-browser only */ } };
const hm = (min) => { const m = Math.max(0, Math.floor(min)); return m >= 60 ? `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}` : `${m} min`; };
const clock = (ms) => { const t = Math.max(0, Math.floor(ms / 1000)); const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), s = t % 60; return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`; };
const btn = { fontSize: 12, padding: '4px 10px' };
const storage = { getItem: (k) => { try { return localStorage.getItem(k); } catch { return null; } }, setItem: (k, v) => { try { localStorage.setItem(k, v); } catch { /* per-browser only */ } } };
const outbox = createOutbox({ storage, call: (op) => ownerCall(op), key: OUTBOX });
let channel = null;
try { channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('oike-timer') : null; } catch { channel = null; }

export function startWork(task) { window.dispatchEvent(new CustomEvent('oike:timer', { detail: { action: 'start', taskId: task?.id || null, title: task?.title || '' } })); }

export default function TimerBar() {
  const [st, setSt] = useState(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(() => outbox.pending());
  const [note, setNote] = useState('');
  const [now, setNow] = useState(Date.now());
  const [skew, setSkew] = useState(0);
  const [unplanned, setUnplanned] = useState(null);
  const [ack, setAck] = useState(() => readJson(ACK, {}));
  const busy = useRef(false);
  const stRef = useRef(null);
  const skewRef = useRef(0);

  const publish = (s) => { stRef.current = s; setSt(s); window.dispatchEvent(new CustomEvent('oike:timer-state', { detail: s })); };
  const load = useCallback(async () => {
    try {
      const s = await ownerCall({ action: 'timerState', timeZone: tz() });
      const k = Date.parse(s.serverNow) - Date.now(); skewRef.current = k; setSkew(k);
      publish(s); setError('');
    } catch (e) { setError(e.code === 'schema_pending' ? 'schema' : !e.status ? 'offline' : e.message); }
  }, []);

  // Send the queued actions in order; without connection they wait (the server never duplicates them and never
  // lets an old one override a later decision: it answers stale or conflict, and the bar says so)
  const flush = useCallback(async () => {
    if (busy.current || !outbox.pending().length) return;
    busy.current = true;
    const r = await outbox.flush();
    busy.current = false;
    setPending(outbox.pending());
    if (r.errors.length) setError(r.errors[0]);
    if (r.stale.length || r.conflicts.length) setNote(r.stale.length
      ? 'Una acción guardada sin conexión no se aplicó: después hubo otro cambio en el contador (por ejemplo, desde otra pestaña o dispositivo).'
      : 'Un tramo quedó «por confirmar»: una acción guardada sin conexión indicaba que terminaste antes. Revísalo en Mi tiempo.');
    if (r.sent) { await load(); try { channel?.postMessage('changed'); } catch { /* other tabs reload on focus anyway */ } }
  }, [load]);

  const act = useCallback(async (body) => {
    const op = { ...body, at: new Date(Date.now() + skewRef.current).toISOString(), timeZone: tz() };
    const { title, ...toSend } = op;
    outbox.enqueue(toSend); setPending(outbox.pending());
    publish(optimistic(stRef.current, op)); // shown at once; the server confirms it on the next read
    await flush();
  }, [flush]);

  useEffect(() => {
    load(); flush();
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => { flush(); load(); }, 60000);
    const onVisible = () => { if (document.visibilityState === 'visible') { flush(); load(); } };
    const onStart = (e) => { const d = e.detail || {}; if (d.action === 'start') act({ action: 'timerStart', taskId: d.taskId, title: d.title, note: d.note || '' }); };
    const onMsg = () => load();
    window.addEventListener('online', flush);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('oike:timer', onStart);
    channel?.addEventListener('message', onMsg);
    return () => { clearInterval(tick); clearInterval(poll); window.removeEventListener('online', flush); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('oike:timer', onStart); channel?.removeEventListener('message', onMsg); };
  }, [act]);

  if (error === 'schema') return null; // fields not created yet: Mi tiempo says what is missing
  if (!st) return null;
  const run = st.running;
  const review = st.settings?.reviewMinutes || 240;
  const ns = run ? noticeState({ startedAt: run.startedAt, now: now + skew, noticeMinutes: st.settings?.noticeMinutes || 90, reviewMinutes: review, ackStep: ack[run.id] || 0 }) : { elapsedMs: 0, elapsedMinutes: 0, step: 0, show: false, overReview: false };
  const elapsedMs = ns.elapsedMs, elapsedMin = ns.elapsedMinutes, step = ns.step, showNotice = ns.show;
  const acknowledge = () => { const next = { [run.id]: step }; setAck(next); writeJson(ACK, next); };
  const label = run ? (run.taskTitle || (run.taskId ? 'Tarea' : run.note || 'Sin tarea (imprevisto)')) : '';

  return (
    <div role="region" aria-label="Contador" style={{ position: 'sticky', top: 0, zIndex: 50, margin: '0 0 12px', padding: '8px 12px', borderRadius: 10, background: run ? 'linear-gradient(rgba(91,191,181,0.14), rgba(91,191,181,0.14)), var(--globant-card, #fff)' : 'var(--globant-card, #fff)', boxShadow: '0 2px 6px rgba(30,42,44,0.06)', border: `1px solid ${run ? 'rgba(91,191,181,0.5)' : 'var(--globant-border)'}`, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
      {run ? (
        <>
          <span aria-hidden="true">⏱</span>
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }} aria-label="Tiempo del tramo">{clock(elapsedMs)}</span>
          <span style={{ flex: '1 1 160px', minWidth: 0, overflowWrap: 'anywhere' }}>{label}</span>
          <button type="button" className="action-btn" style={btn} onClick={() => act(stopBody(run, 'pause'))}>Pausar</button>
          <button type="button" className="action-btn btn-primary" style={btn} onClick={() => act(stopBody(run, 'finish'))}>Terminar</button>
        </>
      ) : st.paused ? (
        <>
          <span aria-hidden="true">⏸</span>
          <span style={{ flex: '1 1 160px', minWidth: 0 }}>En pausa: {st.paused.taskTitle || st.paused.note || 'sin tarea'}</span>
          <button type="button" className="action-btn btn-primary" style={btn} onClick={() => act({ action: 'timerStart', taskId: st.paused.taskId, title: st.paused.taskTitle, note: st.paused.taskId ? '' : st.paused.note })}>Reanudar</button>
          <button type="button" className="action-btn" style={btn} onClick={() => setUnplanned('')}>Otra cosa</button>
        </>
      ) : (
        <>
          <span aria-hidden="true">⏱</span>
          <span style={{ flex: '1 1 160px', color: 'var(--globant-muted)' }}>Sin contador. Usa «Trabajar en esto» en una tarea de Hoy.</span>
          <button type="button" className="action-btn" style={btn} onClick={() => setUnplanned('')}>Empezar sin tarea</button>
        </>
      )}
      <span style={{ fontSize: 11, color: 'var(--globant-muted)' }} title="Tiempo de hoy: registrado y pendiente de confirmar, por separado">
        Hoy {hm(st.today?.confirmedMinutes || 0)} registradas{st.today?.reviewMinutes ? ` · ${hm(st.today.reviewMinutes)} por confirmar` : ''}{st.reviewCount ? ` · ${st.reviewCount} tramo${st.reviewCount === 1 ? '' : 's'} por revisar en Mi tiempo` : ''}
      </span>
      {pending.length > 0 && <span style={{ fontSize: 11, color: '#9A6212' }}>Sin conexión: {pending.length === 1 ? '1 acción pendiente' : `${pending.length} acciones pendientes`} de guardar (con su hora). Se envían solas al volver la conexión.</span>}
      {error === 'offline' && !pending.length && <span style={{ fontSize: 11, color: '#9A6212' }}>Sin conexión: el contador sigue en el servidor; se actualiza al volver.</span>}
      {error && error !== 'schema' && error !== 'offline' && <span role="alert" style={{ fontSize: 11, color: '#9A6212' }}>{error}</span>}
      {unplanned !== null && (
        <span style={{ display: 'flex', gap: 6, flexBasis: '100%', flexWrap: 'wrap' }}>
          <input className="input" style={{ flex: '1 1 220px', padding: '4px 8px', fontSize: 12, border: '1px solid var(--globant-border)', borderRadius: 6 }} placeholder="Qué es (opcional, lo clasificas después)" maxLength={200} value={unplanned} onChange={e => setUnplanned(e.target.value)} aria-label="Qué es" />
          <button type="button" className="action-btn btn-primary" style={btn} onClick={() => { act({ action: 'timerStart', taskId: null, note: unplanned.trim() || 'Imprevisto' }); setUnplanned(null); }}>Empezar</button>
          <button type="button" className="action-btn" style={btn} onClick={() => setUnplanned(null)}>Cancelar</button>
        </span>
      )}
      {showNotice && (
        <span role="status" style={{ flexBasis: '100%', fontSize: 12, color: 'var(--globant-text)' }}>
          Llevas {hm(elapsedMin)} con esto. Si ya terminaste, pausa o termina; si sigues, no hace falta hacer nada.
          {' '}<button type="button" className="action-btn" style={{ ...btn, fontSize: 11 }} onClick={acknowledge}>Sigo</button>
        </span>
      )}
      {note && <span role="status" style={{ flexBasis: '100%', fontSize: 11, color: '#9A6212' }}>{note} <button type="button" className="action-btn" style={{ ...btn, fontSize: 10 }} onClick={() => setNote('')}>Cerrar</button></span>}
      {run && ns.overReview && <span style={{ flexBasis: '100%', fontSize: 11, color: '#9A6212' }}>Más de {hm(review)}: al terminar, este tramo quedará por confirmar (no se descuenta nada; lo revisas en Mi tiempo).</span>}
    </div>
  );
}

/** The timer state for other screens (which task is running), from the bar's last read. */
let lastState = null;
if (typeof window !== 'undefined') window.addEventListener('oike:timer-state', (e) => { lastState = e.detail; });
export function useTimerState() {
  const [s, setS] = useState(lastState);
  useEffect(() => {
    const on = (e) => setS(e.detail);
    window.addEventListener('oike:timer-state', on);
    return () => window.removeEventListener('oike:timer-state', on);
  }, []);
  return s;
}
